import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { BottomSheetPrimitive } from './bottom-sheet.primitive';
import * as Styled from './bottom-sheet.styles';
import {
  bottomSheetDuration,
  bottomSheetTransitionName,
  dimmerDuration,
  dimmerTransitionName,
} from './bottom-sheet.styles';
import type { BottomSheetProps } from './bottom-sheet.types';

export const BottomSheet = React.forwardRef<HTMLDivElement, BottomSheetProps>(
  (
    {
      children,
      className,
      dimmerTitle,
      focusTrapProps,
      keepBottomSheetInDom,
      onOutsideClick,
      shouldDisableBodyScroll = true,
      show = false,
      showCloseButton = true,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const bottomSheetRef = React.useRef<HTMLDivElement>(null);
    const combinedRef = React.useMemo(() => {
      if (forwardedRef) {
        return typeof forwardedRef === 'function'
          ? { current: null, set: forwardedRef }
          : forwardedRef;
      }
      return bottomSheetRef;
    }, [forwardedRef]);

    const dimmerRef = React.useRef<HTMLDivElement>(null);

    const handleKeyDown = (event: React.KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeModal(event);
      }
    };

    const disableScrolling = (): void => {
      document.body.style.overflow = 'hidden';
    };

    const enableScrolling = (): void => {
      document.body.style.overflow = 'visible';
    };

    const closeModal = (event?: React.SyntheticEvent): void => {
      event?.stopPropagation();
      enableScrolling();
      if (onOutsideClick && event) onOutsideClick(event);
    };

    React.useEffect(() => {
      if (!shouldDisableBodyScroll) {
        return;
      }

      if (show) {
        disableScrolling();
      }

      return () => {
        enableScrolling();
      };
    }, [show, shouldDisableBodyScroll, combinedRef]);

    const { focusTrapOptions: userFocusTrapOptions, ...otherFocusTrapProps } =
      focusTrapProps || {};
    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      clickOutsideDeactivates: Boolean(onOutsideClick),
      escapeDeactivates: Boolean(onOutsideClick),
      fallbackFocus: () => combinedRef.current!,
      returnFocusOnDeactivate: true,
      ...userFocusTrapOptions,
    };

    const BottomSheetTransition = (
      <CSSTransition
        appear={show}
        classNames={bottomSheetTransitionName}
        in={show}
        key="bottomSheet"
        mountOnEnter={!keepBottomSheetInDom}
        timeout={parseInt(bottomSheetDuration)}
        unmountOnExit={!keepBottomSheetInDom}
        nodeRef={combinedRef}
      >
        <FocusTrap
          active={show}
          {...otherFocusTrapProps}
          focusTrapOptions={mergedFocusTrapOptions}
        >
          <BottomSheetPrimitive
            className={className}
            closeModal={closeModal}
            data-show={show}
            onKeyDown={handleKeyDown}
            ref={combinedRef}
            showCloseButton={showCloseButton}
            tabIndex={-1}
            {...props}
          >
            {children}
          </BottomSheetPrimitive>
        </FocusTrap>
      </CSSTransition>
    );

    const DimmerTransition = (
      <CSSTransition
        appear={show}
        classNames={dimmerTransitionName}
        in={show}
        key="dimmer"
        mountOnEnter
        onEnter={() => {
          if (shouldDisableBodyScroll) {
            disableScrolling();
          }
        }}
        onExited={() => {
          if (shouldDisableBodyScroll) {
            enableScrolling();
          }
        }}
        timeout={parseInt(dimmerDuration)}
        unmountOnExit
        nodeRef={dimmerRef}
      >
        <Styled.Dimmer
          $isNotClickable={!onOutsideClick}
          data-qa="bottom-sheet-dimmer"
          data-testid="bottom-sheet-dimmer"
          onClick={closeModal}
          title={dimmerTitle}
          ref={dimmerRef}
        />
      </CSSTransition>
    );

    return (
      <>
        <Portal>{BottomSheetTransition}</Portal>
        {DimmerTransition}
      </>
    );
  }
);

BottomSheet.displayName = 'BottomSheet';
